import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, ShieldCheck, ShieldAlert, Upload, Star } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["Idea", "Planning", "In Progress", "Testing", "Live"];
const PROCEDURES = ["Rhinoplasty", "Liposuction", "Hair Transplant", "Blepharoplasty", "Facelift", "Skin", "Other"];
const CONSENTS = ["Signed", "Pending", "Declined"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function SecureImg({ path, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let active = true;
    if (!path) return;
    api.get(`/patient/file?path=${encodeURIComponent(path)}`, { responseType: "blob" })
      .then((r) => { if (active) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className={`bg-muted flex items-center justify-center text-xs text-muted-foreground ${className}`}>No image</div>;
  return <img src={src} alt="case" className={className} />;
}

export default function PatientExperience() {
  const { centers } = useApp();
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [cards, setCards] = useState([]);
  const [cases, setCases] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [procFilter, setProcFilter] = useState("all");
  const [caseOpen, setCaseOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, b, f] = await Promise.all([api.get("/ux-projects"), api.get("/before-after"), api.get("/feedback")]);
    setCards(c.data); setCases(b.data); setFeedback(f.data);
  };
  useEffect(() => { load(); }, []);

  const moveCard = async (card, stage) => {
    try { await api.put(`/ux-projects/${card.id}`, { ...card, stage }); load(); }
    catch (e) { toast.error("Move failed"); }
  };

  const saveCase = async () => {
    setSaving(true);
    try {
      const c = centers.find((x) => x.id === form.center_id);
      const payload = { ...form, center_code: c?.code };
      if (form.id) await api.put(`/before-after/${form.id}`, payload);
      else { const r = await api.post("/before-after", payload); payload.id = r.data.id; setForm(payload); }
      toast.success("Case saved"); load();
      if (!form.id) toast.message("Now upload before/after images below");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (bid, kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/before-after/${bid}/upload?kind=${kind}`, fd); toast.success(`${kind} uploaded`); load(); }
    catch (e) { toast.error("Upload failed"); }
  };

  const togglePublish = async (c) => {
    try { await api.put(`/before-after/${c.id}`, { ...c, publish_ready: !c.publish_ready }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const gallery = procFilter === "all" ? cases : cases.filter((c) => c.procedure === procFilter);

  return (
    <div data-testid="patient-experience-page">
      <PageHeader eyebrow="Patient Journey" title="Patient Experience Solutions"
        subtitle="UX project board, consent-controlled before/after showcase, and patient feedback." />

      <Tabs defaultValue="board">
        <TabsList>
          <TabsTrigger value="board" data-testid="tab-board">Project Board</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Before/After Showcase</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {STAGES.map((stage) => (
              <div key={stage} className="space-y-2" data-testid={`kanban-${stage.replace(/ /g, "-")}`}>
                <div className="flex items-center justify-between px-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{stage}</p>
                  <Badge variant="secondary" className="text-[10px]">{cards.filter((c) => c.stage === stage).length}</Badge></div>
                {cards.filter((c) => c.stage === stage).map((c) => (
                  <Card key={c.id} className="p-3" data-testid={`card-${c.id}`}>
                    <p className="text-sm font-medium">{c.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{c.owner}</p>
                    <Select value={c.stage} onValueChange={(v) => moveCard(c, v)}>
                      <SelectTrigger className="h-7 mt-2 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="gallery" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Select value={procFilter} onValueChange={setProcFilter}>
              <SelectTrigger className="w-[200px]" data-testid="procedure-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Procedures</SelectItem>{PROCEDURES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => { setForm({ procedure: "Rhinoplasty", center_id: centers[0]?.id, consent_status: "Pending", anonymized: true, approval_status: "Pending", date: new Date().toISOString().slice(0, 10) }); setCaseOpen(true); }} data-testid="add-case-button" style={{ display: canWrite ? undefined : "none" }}>
              <Plus className="h-4 w-4 mr-1" /> Add Case
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gallery.map((c) => (
              <Card key={c.id} className="overflow-hidden" data-testid={`case-${c.id}`}>
                <div className="grid grid-cols-2 h-40">
                  <SecureImg path={c.before_image_path} className="h-40 w-full object-cover border-r border-border" />
                  <SecureImg path={c.after_image_path} className="h-40 w-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{c.procedure}</p>
                    {c.consent_status === "Signed"
                      ? <span className="flex items-center gap-1 text-[11px] text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Consent signed</span>
                      : <span className="flex items-center gap-1 text-[11px] text-amber-600"><ShieldAlert className="h-3.5 w-3.5" /> {c.consent_status}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{c.doctor} · {c.center_code} · {c.date}</p>
                  <div className="flex items-center justify-between mt-3">
                    <label className="flex items-center gap-2 text-xs">
                      <Switch checked={!!c.publish_ready} onCheckedChange={() => togglePublish(c)} data-testid={`publish-${c.id}`} />
                      Publish-ready
                    </label>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setForm(c); setCaseOpen(true); }} data-testid={`edit-case-${c.id}`}>Manage</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="feedback" className="mt-4">
          <div className="grid sm:grid-cols-3 gap-4">
            {feedback.map((f) => (
              <Card key={f.id} className="p-5" data-testid={`feedback-${f.center_code}`}>
                <h3 className="font-display font-semibold mb-3">{f.center_code}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">NPS</span><span className="font-bold">{f.nps}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Reviews</span><span className="font-bold">{f.review_count}</span></div>
                  <div className="flex justify-between items-center"><span className="text-muted-foreground">Google</span><span className="font-bold flex items-center gap-1">{f.google_rating} <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /></span></div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={caseOpen} onOpenChange={setCaseOpen}>
        <DialogContent data-testid="case-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? "Manage Case" : "Add Before/After Case"}</DialogTitle>
            <DialogDescription>Only cases with Signed consent can be marked Publish-ready.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Procedure</Label>
                <Select value={form.procedure} onValueChange={(v) => setForm({ ...form, procedure: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PROCEDURES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Doctor</Label><Input value={form.doctor || ""} onChange={(e) => setForm({ ...form, doctor: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Center</Label>
                <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Consent status</Label>
                <Select value={form.consent_status} onValueChange={(v) => setForm({ ...form, consent_status: v })}><SelectTrigger data-testid="consent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONSENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.anonymized} onCheckedChange={(v) => setForm({ ...form, anonymized: v })} /> Face blurred / anonymized</label>
            <Button onClick={saveCase} disabled={saving || !form.doctor} className="w-full" data-testid="save-case-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.id ? "Save case" : "Create case")}</Button>

            {form.id && (
              <div className="pt-3 border-t border-border space-y-2">
                <p className="text-xs font-medium">Uploads</p>
                {[["before", "Before image"], ["after", "After image"], ["consent", "Consent form"]].map(([kind, label]) => (
                  <label key={kind} className="flex items-center gap-2 text-xs cursor-pointer border border-dashed border-border rounded-lg p-2 hover:bg-secondary">
                    <Upload className="h-4 w-4" /> {label}
                    <input type="file" className="hidden" data-testid={`upload-${kind}`} onChange={(e) => e.target.files[0] && upload(form.id, kind, e.target.files[0])} />
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCaseOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
